// File: src/pages/Careers.jsx

import React, { useRef, useEffect, useState } from "react";
import CompanyHeader from "./CompanyHeader";
import FooterLinks from "../LandingPage/FooterLinks";
import SeoMeta from "../SEO/SeoMeta";

export default function Careers() {
  const headerRef = useRef(null);
  const [headerHeight, setHeaderHeight] = useState(0);

  // Dynamically measure header height
  useEffect(() => {
    function updateHeight() {
      if (headerRef.current) setHeaderHeight(headerRef.current.offsetHeight);
    }
    updateHeight();
    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, []);

  return (
    <div className="relative min-h-screen w-full bg-white text-black">
      <SeoMeta
        title="Careers at Million2Trillion — Build the Future of Verified Financial Markets"
        description="Join Million2Trillion and help build a global verified financial ecosystem. Explore open roles in technology, compliance, content creation, community management, and more."
        keywords="Million2Trillion Careers, Financial Platform Jobs, Global Fintech Hiring, Verified Financial Ecosystem, KYC Jobs, AI Financial Developer, Trading Platform Careers"
        url="https://million2trillion.com/careers"
      />

      {/* Fixed Header */}
      <CompanyHeader ref={headerRef} activePage="careers" />

      {/* Spacer to push content below fixed header */}
      <div style={{ height: headerHeight }} aria-hidden="true" />

      {/* Banner image, same as Team */}
      <div className="w-full bg-white">
        <img
          src="/assets/company/Million2Trillion.gif"
          alt="About Million2Trillion"
          className="block w-full"
          style={{
            width: "100vw",
            maxWidth: "100vw",
            height: "auto",
          }}
          draggable={false}
        />
      </div>

      <main className="mx-auto max-w-6xl space-y-16 px-6 py-16 md:px-20">
        {/* Hero Section */}
        <header className="space-y-4 text-center">
          <h1 className="text-4xl font-extrabold text-black">
            Careers at Million2Trillion
          </h1>
          <p className="mx-auto max-w-2xl text-lg text-gray-700">
            Build with us. Innovate. Empower. And help shape the verified global
            financial economy.
          </p>
        </header>

        {/* Careers Open Roles */}
        <section
          className="mx-auto max-w-5xl space-y-8 rounded-2xl border border-gray-200 bg-gradient-to-br from-gray-100 via-white to-gray-100 p-10 shadow-2xl"
          aria-labelledby="open-positions"
        >
          <h2
            id="open-positions"
            className="mb-6 text-center text-3xl font-bold text-purple-400"
          >
            Current Open Positions
          </h2>

          <div className="space-y-10">
            {[
              {
                title: "Full Stack Developer",
                subtitle: "React, Firebase, Node.js, AI Integrations",
                responsibilities: [
                  "Develop and maintain platform modules",
                  "Integrate AI models, verification flows, and financial APIs",
                  "Build secure role-based infrastructure for verified members",
                ],
                requirements: [
                  "Strong React.js + Firebase skills",
                  "Experience with Node.js, REST APIs",
                  "Knowledge in AI/ML model deployment (bonus)",
                ],
              },
              {
                title: "Market Research Analyst",
                subtitle: "Global Financial Markets · Data Trends",
                responsibilities: [
                  "Analyze global financial data across all market sectors",
                  "Monitor industry shifts, trading volumes, regulations",
                  "Support our verified role onboarding structure",
                ],
                requirements: [
                  "Knowledge of Forex, Stocks, Commodities, Crypto",
                  "Experience with financial datasets and visualization",
                  "Excellent research & report writing skills",
                ],
              },
              {
                title: "Compliance & KYC Officer",
                subtitle: "Trust Economy · Member Verification",
                responsibilities: [
                  "Oversee member KYC, document checks, onboarding",
                  "Manage identity verification standards for multi-role access",
                  "Work with legal & global compliance teams",
                ],
                requirements: [
                  "Experience in KYC/AML compliance procedures",
                  "Knowledge of global regulatory standards",
                  "Attention to detail & high security focus",
                ],
              },
              {
                title: "Content & Education Creator",
                subtitle: "Build Financial Knowledge Base for Global Users",
                responsibilities: [
                  "Develop video, text, and course-based financial content",
                  "Collaborate with verified educators and market professionals",
                  "Build role-specific knowledge hubs",
                ],
                requirements: [
                  "Strong educational writing & communication",
                  "Experience building LMS, YouTube or course content",
                  "Excellent understanding of global financial education",
                ],
              },
              {
                title: "Community Manager",
                subtitle: "User Growth · Member Success",
                responsibilities: [
                  "Manage verified member onboarding",
                  "Handle user queries, chats, member education",
                  "Support growth, feedback loops, and internal reporting",
                ],
                requirements: [
                  "Excellent communication and people skills",
                  "Experience with SaaS, fintech or trading platforms",
                  "Multi-language skills (bonus)",
                ],
              },
            ].map((job, index) => (
              <article
                key={index}
                className="space-y-4 rounded-xl border border-gray-300 bg-white p-8 shadow-lg"
              >
                <h3 className="text-2xl font-bold text-purple-500">
                  {job.title}
                </h3>
                <p className="italic text-gray-700">{job.subtitle}</p>

                <div className="space-y-2 text-gray-700">
                  <p>
                    <strong>Responsibilities:</strong>
                  </p>
                  <ul className="ml-6 list-disc">
                    {job.responsibilities.map((item, i) => (
                      <li key={i}>{item}</li>
                    ))}
                  </ul>
                  <p>
                    <strong>Requirements:</strong>
                  </p>
                  <ul className="ml-6 list-disc">
                    {job.requirements.map((item, i) => (
                      <li key={i}>{item}</li>
                    ))}
                  </ul>
                </div>
              </article>
            ))}
          </div>

          <p className="pt-10 text-center text-gray-700">
            You’re not just joining a job — you're contributing to{" "}
            <span className="font-semibold text-purple-400">
              Million2Trillion’s Global Trust Revolution.
            </span>
          </p>
        </section>

        {/* CTA */}
        <section className="space-y-4 pt-12 text-center">
          <h2 className="text-3xl font-bold text-purple-400">
            Ready To Join Our Global Mission?
          </h2>
          <div className="space-x-4">
            <a
              href="https://mail.google.com/mail/?view=cm&fs=1&to=recruitment@million2trillion.com"
              target="_blank"
              rel="noopener noreferrer"
              className="mx-auto block w-fit rounded-full bg-gradient-to-r from-purple-500 to-blue-500 px-8 py-4 text-lg font-bold text-white shadow-lg transition hover:from-purple-600 hover:to-blue-600"
            >
              Please send your CV to recruitment@million2trillion.com
            </a>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="mt-36 border-t border-gray-700 bg-black">
        <FooterLinks />
      </footer>
    </div>
  );
}
